import { z } from 'zod'

/**
 * Status do caso de cobrança (máquina de estados do plano §4). Fonte única: o banco (enum
 * `collection_status`), a API e o catálogo visual do `@gestao/ui` (cor, ícone, rótulo) usam esta lista.
 */
export const COLLECTION_STATUSES = [
  'novo',
  'em_contato',
  'sem_contato',
  'em_negociacao',
  'promessa_pagamento',
  'acordo_firmado',
  'acordo_em_andamento',
  'acordo_quebrado',
  'quitado',
  'cancelado',
  'juridico',
  'bloqueado',
  'recorrente',
] as const

export type CollectionStatus = (typeof COLLECTION_STATUSES)[number]

export const CollectionStatusSchema = z.enum(COLLECTION_STATUSES, {
  error: 'Status de cobrança desconhecido.',
})

/**
 * Rótulos em pt-BR (os mesmos do catálogo visual de `@gestao/ui`), para textos gerados na API, como
 * a exportação da carteira.
 */
export const COLLECTION_STATUS_LABELS: Readonly<Record<CollectionStatus, string>> = {
  novo: 'Novo',
  em_contato: 'Em contato',
  sem_contato: 'Sem contato',
  em_negociacao: 'Em negociação',
  promessa_pagamento: 'Promessa de pagamento',
  acordo_firmado: 'Acordo firmado',
  acordo_em_andamento: 'Acordo em andamento',
  acordo_quebrado: 'Acordo quebrado',
  quitado: 'Quitado',
  cancelado: 'Cancelado',
  juridico: 'Jurídico',
  bloqueado: 'Bloqueado',
  recorrente: 'Recorrente',
}

export function isCollectionStatus(value: string): value is CollectionStatus {
  return (COLLECTION_STATUSES as readonly string[]).includes(value)
}

/** Status que encerram o caso (`closedAt` preenchido). Casos fechados ficam como histórico. */
export const CLOSED_COLLECTION_STATUSES = [
  'quitado',
  'cancelado',
] as const satisfies readonly CollectionStatus[]

/** Status de caso aberto (todos menos os que encerram), na ordem canônica. */
export const OPEN_COLLECTION_STATUSES: readonly CollectionStatus[] = COLLECTION_STATUSES.filter(
  (status) => !(CLOSED_COLLECTION_STATUSES as readonly string[]).includes(status),
)

/** Saídas possíveis de qualquer status aberto: encerrar sem cobrança, judicial ou bloquear. */
const EXITS = ['cancelado', 'juridico', 'bloqueado'] as const satisfies readonly CollectionStatus[]

/**
 * Transições válidas (plano §4): `novo → em_contato → sem_contato | em_negociacao →
 * promessa_pagamento | acordo_firmado → acordo_em_andamento → quitado | acordo_quebrado → em_contato`,
 * com saídas `cancelado`, `juridico`, `bloqueado` e `recorrente` (reincidência após quitado).
 *
 * `promessa_pagamento` também é alcançável direto de `novo`, `em_contato`, `sem_contato`,
 * `acordo_quebrado` e `recorrente`: a promessa registrada no atendimento muda o caso na hora
 * (Sprint 3, decisions.md).
 *
 * `acordo_firmado` também é alcançável de `novo`, `em_contato`, `sem_contato`, `recorrente` e
 * `acordo_quebrado`: acordo no primeiro contato e acordo novo depois de um quebrado (Sprint 4).
 * As transições de sistema do estorno (`quitado → acordo_em_andamento`, `acordo_em_andamento →
 * acordo_firmado`) não entram aqui: só o serviço de pagamentos as grava (`payment_reversal`).
 *
 * `quitado` é alcançável de qualquer status aberto porque a baixa pode chegar do ERP a qualquer
 * momento. Toda transição grava quem, quando e por quê (`CaseStatusTransition`).
 */
export const CASE_STATUS_TRANSITIONS: Readonly<
  Record<CollectionStatus, readonly CollectionStatus[]>
> = {
  novo: ['em_contato', 'sem_contato', 'promessa_pagamento', 'acordo_firmado', 'quitado', ...EXITS],
  em_contato: [
    'sem_contato',
    'em_negociacao',
    'promessa_pagamento',
    'acordo_firmado',
    'quitado',
    ...EXITS,
  ],
  sem_contato: ['em_contato', 'promessa_pagamento', 'acordo_firmado', 'quitado', ...EXITS],
  em_negociacao: ['promessa_pagamento', 'acordo_firmado', 'em_contato', 'quitado', ...EXITS],
  promessa_pagamento: ['acordo_firmado', 'em_negociacao', 'em_contato', 'quitado', ...EXITS],
  acordo_firmado: ['acordo_em_andamento', 'acordo_quebrado', 'quitado', ...EXITS],
  acordo_em_andamento: ['acordo_quebrado', 'quitado', ...EXITS],
  acordo_quebrado: [
    'em_contato',
    'em_negociacao',
    'promessa_pagamento',
    'acordo_firmado',
    'quitado',
    ...EXITS,
  ],
  quitado: ['recorrente'],
  cancelado: [],
  juridico: ['acordo_firmado', 'quitado', 'cancelado'],
  bloqueado: ['em_contato', 'quitado', 'cancelado'],
  recorrente: [
    'em_contato',
    'sem_contato',
    'promessa_pagamento',
    'acordo_firmado',
    'quitado',
    ...EXITS,
  ],
}

export function canTransition(from: CollectionStatus | null, to: CollectionStatus): boolean {
  if (from === null) return to === 'novo' || to === 'recorrente'
  return CASE_STATUS_TRANSITIONS[from].includes(to)
}

export const CASE_PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const
export type CasePriority = (typeof CASE_PRIORITIES)[number]
export const CasePrioritySchema = z.enum(CASE_PRIORITIES)

export const CASE_PRIORITY_LABELS: Readonly<Record<CasePriority, string>> = {
  low: 'Baixa',
  normal: 'Normal',
  high: 'Alta',
  urgent: 'Urgente',
}

/**
 * Prioridade inicial do caso pelo valor vencido (sprint-1-spec §3): a partir de R$ 3.000 urgente,
 * a partir de R$ 1.000 alta, abaixo disso normal. Recebe centavos para não arredondar.
 */
export function priorityForOverdueCents(overdueCents: number): CasePriority {
  if (overdueCents >= 300_000) return 'urgent'
  if (overdueCents >= 100_000) return 'high'
  return 'normal'
}

export const CASE_BLOCKED_REASONS = ['deceased', 'inactive'] as const
export type CaseBlockedReason = (typeof CASE_BLOCKED_REASONS)[number]
export const CaseBlockedReasonSchema = z.enum(CASE_BLOCKED_REASONS)

export const CASE_BLOCKED_REASON_LABELS: Readonly<Record<CaseBlockedReason, string>> = {
  deceased: 'Cliente falecido',
  inactive: 'Cliente inativo no ERP',
}

export const CASE_TRANSITION_SOURCES = [
  'system',
  'user',
  'import',
  'agreement',
  'payment',
  'payment_reversal',
  'agreement_job',
  /** Sprint 5: mesclagem de cadastros e sua reversão. */
  'merge',
  /** Sprint 5: carga universal (planilha/texto colado) e cadastro manual. */
  'import_universal',
  /** Sprint 5: distribuição equilibrada da carteira. */
  'distribution',
] as const
export type CaseTransitionSource = (typeof CASE_TRANSITION_SOURCES)[number]
export const CaseTransitionSourceSchema = z.enum(CASE_TRANSITION_SOURCES)

/** Faixa de aging em dias de atraso; `max: null` é aberta (">180"). */
export const AgingBandSchema = z
  .object({
    min: z.number().int().min(1),
    max: z.number().int().min(1).nullable(),
  })
  .refine((band) => band.max === null || band.max >= band.min, {
    error: 'O fim da faixa precisa ser maior ou igual ao início.',
  })
export type AgingBand = z.infer<typeof AgingBandSchema>

export const AgingBandsSchema = z
  .array(AgingBandSchema)
  .min(1)
  .refine(
    (bands) =>
      bands.every((band, index) => {
        const previous = bands[index - 1]
        if (!previous) return band.min === 1
        return previous.max !== null && band.min === previous.max + 1
      }) && bands.at(-1)?.max === null,
    { error: 'As faixas precisam começar em 1, ser contínuas e terminar abertas.' },
  )

/** Chave da configuração (`Setting`) com as faixas de aging. */
export const AGING_BANDS_SETTING_KEY = 'aging.bands'

/** Faixas padrão (design-system §5): 1–30, 31–60, 61–90, 91–180, >180. */
export const DEFAULT_AGING_BANDS: readonly AgingBand[] = Object.freeze([
  { min: 1, max: 30 },
  { min: 31, max: 60 },
  { min: 61, max: 90 },
  { min: 91, max: 180 },
  { min: 181, max: null },
])

/** Rótulo da faixa em pt-BR: "1 a 30" ou ">180". */
export function agingBandLabel(band: AgingBand): string {
  return band.max === null ? `>${band.min - 1}` : `${band.min} a ${band.max}`
}

/**
 * Rótulo da faixa nos relatórios, que também é o valor da dimensão "Faixa de atraso": "1 a 30
 * dias" ou, na última faixa, "Mais de 180 dias".
 */
export function reportAgingBandLabel(band: AgingBand): string {
  return band.max === null ? `Mais de ${band.min - 1} dias` : `${band.min} a ${band.max} dias`
}

// --- Prazos da cobrança (Sprint 16.1, D21) ----------------------------------------------------------

/** Chave da configuração (`Setting`) com os prazos da cobrança (Configurações › Parâmetros). */
export const COLLECTION_THRESHOLDS_SETTING_KEY = 'collection.thresholds'

const thresholdDays = (max: number) =>
  z
    .number({ error: 'Informe o número de dias.' })
    .int({ error: 'Informe um número inteiro de dias.' })
    .min(1, { error: 'Use pelo menos 1 dia.' })
    .max(max, { error: `Use no máximo ${max.toLocaleString('pt-BR')} dias.` })

/**
 * Prazos da cobrança num lugar só (D21): lidos pela faceta "Sem contato há", pelo KPI "Sem contato"
 * do painel, pela regra de alerta `no_contact`, pelo relatório "Clientes sem retorno", pelo filtro
 * rápido "Críticos" e pelos relatórios de recorrentes.
 *
 * - `noContactDays`: caso sem contato efetivo há N dias ou mais;
 * - `criticalDaysOverdue`: caso crítico com atraso ACIMA de N dias (ou prioridade urgente);
 * - `recurrentDaysOverdue`: recorrente em atraso com atraso ACIMA de N dias;
 * - `firmCollectionDays`: cobrança firme A PARTIR de N dias de atraso.
 */
export const CollectionThresholdsSchema = z.object({
  noContactDays: thresholdDays(365),
  criticalDaysOverdue: thresholdDays(3650),
  recurrentDaysOverdue: thresholdDays(3650),
  firmCollectionDays: thresholdDays(3650),
})
export type CollectionThresholds = z.infer<typeof CollectionThresholdsSchema>

/** Padrões (D21): sem contato 30 dias, crítico acima de 90, recorrente acima de 120, firme a partir de 46. */
export const DEFAULT_COLLECTION_THRESHOLDS: Readonly<CollectionThresholds> = Object.freeze({
  noContactDays: 30,
  criticalDaysOverdue: 90,
  recurrentDaysOverdue: 120,
  firmCollectionDays: 46,
})

/**
 * Prazos da configuração gravada: valor válido inteiro; parcial ou inválido completa/volta ao
 * padrão campo a campo. `fromSetting` diz se algum campo veio da configuração.
 */
export function resolveCollectionThresholds(value: unknown): {
  thresholds: CollectionThresholds
  fromSetting: boolean
} {
  const whole = CollectionThresholdsSchema.safeParse(value)
  if (whole.success) return { thresholds: whole.data, fromSetting: true }
  const thresholds: CollectionThresholds = { ...DEFAULT_COLLECTION_THRESHOLDS }
  let fromSetting = false
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const record = value as Record<string, unknown>
    for (const key of Object.keys(
      DEFAULT_COLLECTION_THRESHOLDS,
    ) as (keyof CollectionThresholds)[]) {
      const parsed = CollectionThresholdsSchema.shape[key].safeParse(record[key])
      if (parsed.success) {
        thresholds[key] = parsed.data
        fromSetting = true
      }
    }
  }
  return { thresholds, fromSetting }
}

// --- Dia de vencimento (Sprint 16.1, D20) -----------------------------------------------------------

/** Último dia do mês da data `AAAA-MM-DD`. */
export function lastDayOfMonthOf(date: string): number {
  const year = Number(date.slice(0, 4))
  const month = Number(date.slice(5, 7))
  return new Date(Date.UTC(year, month, 0)).getUTCDate()
}

/**
 * Dia em que o vencimento do dia `dueDay` cai no mês da data: o próprio dia, ou o último dia do mês
 * quando o mês é mais curto (quem vence dia 29, 30 ou 31 vence no dia 28 de fevereiro).
 */
export function effectiveDueDay(dueDay: number, date: string): number {
  return Math.min(dueDay, lastDayOfMonthOf(date))
}

/** O vencimento do dia `dueDay` cai na data `AAAA-MM-DD`. */
export function isDueOn(dueDay: number, date: string): boolean {
  return effectiveDueDay(dueDay, date) === Number(date.slice(8, 10))
}

/** Soma dias a uma data `AAAA-MM-DD` (calendário, sem fuso). */
function plusDays(date: string, days: number): string {
  const [year, month, day] = date.split('-').map(Number) as [number, number, number]
  return new Date(Date.UTC(year, month - 1, day + days)).toISOString().slice(0, 10)
}

/** O vencimento do dia `dueDay` cai entre hoje e hoje + `days` (hoje incluído). */
export function isDueWithin(dueDay: number, today: string, days: number): boolean {
  for (let offset = 0; offset <= days; offset += 1) {
    if (isDueOn(dueDay, plusDays(today, offset))) return true
  }
  return false
}

/**
 * Dias de vencimento (1 a 31) que caem entre hoje e hoje + `days`, para montar a condição SQL:
 * `exact` são os dias exatos e `fromDay`, quando presente, quer dizer "este dia ou depois" (a
 * janela passa pelo último dia de um mês curto). Mesma regra de `isDueWithin`.
 */
export function dueDaysWithin(
  today: string,
  days: number,
): { exact: number[]; fromDay: number | null } {
  const exact = new Set<number>()
  let fromDay: number | null = null
  for (let offset = 0; offset <= days; offset += 1) {
    const date = plusDays(today, offset)
    const day = Number(date.slice(8, 10))
    if (day === lastDayOfMonthOf(date)) fromDay = fromDay === null ? day : Math.min(fromDay, day)
    else exact.add(day)
  }
  const list = [...exact].filter((day) => fromDay === null || day < fromDay).sort((a, b) => a - b)
  return { exact: list, fromDay }
}

/** Janela do filtro rápido "Vence em até 3 dias" (hoje incluído). */
export const DUE_SOON_WINDOW_DAYS = 3
